import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import Skeleton from '@material-ui/lab/Skeleton';
import cx from 'classnames';
import { includes, isEmpty, isNil, isNull } from 'lodash';
import {
  objectiveTranscriptionAndSolutionCreate,
  transcriptionAndSolutionDelete,
  transcriptionsAndSolutionsCheckHasResults,
  transcriptionsAndSolutionsManualRetry,
  transcriptionsAndSolutionsUpdate,
  useAssignmenTasks,
  useTranscriptionsAndSolutionsCheckShouldRetry,
  useTranscriptionsAndSolutionsResults
} from 'sdk';

import Stepper from 'pages/Teachers/shared/AssignmentDialog/components/Stepper';
import TranscriptionsAndSolutionsErrorMessageBox from 'pages/Teachers/shared/AssignmentDialog/components/TranscriptionsAndSolutionsErrorMessageBox';
import { deltaToMarkdown } from 'pages/Teachers/shared/AssignmentDialog/parser';
import PreviewField from 'pages/Teachers/shared/PreviewField';
import EditDialog from 'pages/Teachers/shared/TranscriptionsAndSolutionsEditDialog';
import QuestionField from 'pages/Teachers/shared/TranscriptionsAndSolutionsMarkdownField';
import { colors } from 'theme/palette';
import { notifyError, notifyErrors, notifySuccess } from 'utils/notifications';

import Button from 'components/Button';
import Typography from 'components/Typography';

import { ReactComponent as DeleteIcon } from './assets/delete-icon.svg';
import { ReactComponent as EditIcon } from './assets/edit-icon.svg';
import { ReactComponent as AddIcon } from './assets/plus-icon.svg';
import styles from './styles.module.scss';

const LATEX_EDITOR_ENABLED =
  process.env.REACT_APP_LATEX_EDITOR_ENABLED === 'true';
const CREATE_ASSIGNMENT_NEW_FLOW_ENABLED =
  process.env.REACT_APP_CREATE_ASSIGNMENT_NEW_FLOW_ENABLED === 'true';

const Editor = ({
  goToNextStep,
  goToBackStep,
  trackerId,
  activeStep,
  onComplete,
  showStepper = true
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [completedQuestions, setCompletedQuestions] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [addNewTranscriptionAndSolution, setAddNewTranscriptionAndSolution] =
    useState(false);

  const [currentPage, setCurrentPage] = useState(null);

  const [pages, setPages] = useState([]);
  const [groupedDataByPages, setGroupedDataByPages] = useState([]);

  const [isCheckingProgress, setIsCheckingProgress] = useState(false);

  const { data: assignmentPages } = useAssignmenTasks({
    assignmentId: trackerId
  });

  const [shouldRetry, setShouldRetry] = useState(false);

  const { data: shouldRetryData, refetch: shouldRetryDataRefetch } =
    useTranscriptionsAndSolutionsCheckShouldRetry({
      trackerId: trackerId
    });

  const [transcriptionsAndSolutionsData, setTranscriptionsAndSolutionsData] =
    useState(null);

  useEffect(() => {
    if (!isNil(shouldRetryData)) {
      setShouldRetry(shouldRetryData['should_retry']);
    }
  }, [shouldRetryData, shouldRetry]);

  const allQuestionsAreCompleted = useMemo(() => {
    const result =
      Object.keys(completedQuestions).every((pageId) => {
        const completedIndices = completedQuestions[pageId] || [];
        const totalQuestions = groupedDataByPages[pageId]?.length || 0;

        return (
          completedIndices.length > 0 &&
          completedIndices.length === totalQuestions
        );
      }) &&
      !isEmpty(completedQuestions) &&
      Object.keys(groupedDataByPages).length ===
        Object.keys(completedQuestions).length;

    return result;
  }, [completedQuestions, groupedDataByPages]);

  const {
    data,
    refetch: refetchTranscriptionsAndSolutions,
    isLoading: isLoadingTranscriptionsAndSolutions
  } = useTranscriptionsAndSolutionsResults({ trackerId });

  useEffect(() => {
    refetchTranscriptionsAndSolutions();
  }, [refetchTranscriptionsAndSolutions, activeStep]);

  useEffect(() => {
    if (!data) return;
    const groupedData = data.reduce((acc, item) => {
      const { objective_id } = item;
      if (!acc[objective_id]) {
        acc[objective_id] = [];
      }
      acc[objective_id].push(item);
      return acc;
    }, {});

    setGroupedDataByPages(groupedData);
  }, [data]);

  useEffect(() => {
    if (assignmentPages?.length > 0) {
      setPages(assignmentPages);
      setCurrentPage(assignmentPages[0]);
    }
  }, [assignmentPages]);

  useEffect(() => {
    if (currentPage) {
      const currentPageData = groupedDataByPages[currentPage.id];
      if (currentPageData) {
        setTranscriptionsAndSolutionsData(currentPageData);
      } else {
        setTranscriptionsAndSolutionsData([]);
      }
    }
  }, [currentPage, groupedDataByPages]);

  const handleEditConfirm = useCallback(
    async ({ transcription, solution }) => {
      if (currentPage !== null) {
        setCompletedQuestions((prev) => {
          if (includes(prev[currentPage.id] || [], activeIndex)) {
            return prev;
          } else {
            return {
              ...prev,
              [currentPage.id]: [...(prev[currentPage.id] || []), activeIndex]
            };
          }
        });
      }

      setEditOpen(false);

      let data = {
        transcription,
        solution
      };

      if (!LATEX_EDITOR_ENABLED) {
        let solutionMarkdown;

        if (solution['ops'] !== undefined) {
          solutionMarkdown = deltaToMarkdown({ deltaData: solution['ops'] });
        }

        data.transcription = {
          transcription: deltaToMarkdown({ deltaData: transcription['ops'] }),
          solution: solutionMarkdown
        };
        data.solution = solutionMarkdown;
      }

      const { success } = await transcriptionsAndSolutionsUpdate({
        objectiveTranscriptionAndSolutionId:
          transcriptionsAndSolutionsData[activeIndex].id,
        data
      });

      if (success) {
        setTranscriptionsAndSolutionsData(null);
        refetchTranscriptionsAndSolutions();
      } else {
        notifyError('Failed to update question and answer. Try again.');
      }
    },
    [
      activeIndex,
      transcriptionsAndSolutionsData,
      currentPage,
      refetchTranscriptionsAndSolutions
    ]
  );

  const handleDelete = async ({ index }) => {
    setGroupedDataByPages((prev) => {
      const updatedPageData = [...(prev[currentPage.id] || [])];
      updatedPageData.splice(index, 1);
      return { ...prev, [currentPage.id]: updatedPageData };
    });

    setTranscriptionsAndSolutionsData((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

    setCompletedQuestions((prev) => {
      const updated = (prev[currentPage.id] || [])
        .filter((idx) => idx !== index)
        .map((idx) => (idx > index ? idx - 1 : idx));
      return { ...prev, [currentPage.id]: updated };
    });

    setActiveIndex(null);

    const { success } = await transcriptionAndSolutionDelete({
      objectiveTranscriptionAndSolutionId:
        transcriptionsAndSolutionsData[index].id
    });

    if (!success) {
      console.error('Delete failed, please try again.');
      refetchTranscriptionsAndSolutions();
    }
  };

  const handlePageChange = (event) => {
    const selectedPage = pages.find((page) => page.id === event.target.value);
    setCurrentPage(selectedPage);
    setActiveIndex(null);
    setTranscriptionsAndSolutionsData(null);
  };

  const handleTranscriptionAndSolutionCreate = useCallback(
    async ({ transcription, solution }) => {
      setAddNewTranscriptionAndSolution(false);
      setEditOpen(false);

      let data = {
        transcription,
        solution
      };

      if (!LATEX_EDITOR_ENABLED) {
        data = {
          transcription: deltaToMarkdown({ deltaData: transcription['ops'] }),
          solution: deltaToMarkdown({ deltaData: solution['ops'] })
        };
      }

      const { success } = await objectiveTranscriptionAndSolutionCreate({
        objectiveId: currentPage.id,
        data
      });

      if (success) {
        setTranscriptionsAndSolutionsData(null);
        refetchTranscriptionsAndSolutions();
      } else {
        notifyError('Failed to create new question and answer. Try again.');
      }
    },
    [currentPage, refetchTranscriptionsAndSolutions]
  );

  const handleConfirmClick = () => {
    if (activeIndex !== null) {
      setCompletedQuestions((prev) => {
        if (includes(prev[currentPage.id] || [], activeIndex)) {
          return prev;
        } else {
          return {
            ...prev,
            [currentPage.id]: [...(prev[currentPage.id] || []), activeIndex]
          };
        }
      });
    }
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setAddNewTranscriptionAndSolution(false);
  };

  const handleTranscriptionAndSolutionManualRetry = async () => {
    setIsCheckingProgress(true);
    setShouldRetry(false);

    const maxAttempts = 180;

    const { success } = await transcriptionsAndSolutionsManualRetry({
      trackerId: trackerId
    });

    if (success) {
      notifySuccess('Questions & Answers retry initiated successfully.');

      try {
        const delay = 3000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const { data, errors } =
            await transcriptionsAndSolutionsCheckHasResults({ trackerId });

          if (!isEmpty(errors)) {
            notifyErrors(errors);
            throw new Error('Error checking fo results.');
          }
          const { is_processing_ready } = data;

          if (is_processing_ready) {
            setIsCheckingProgress(false);
            refetchTranscriptionsAndSolutions();
            shouldRetryDataRefetch();

            if (data && data.length > 0) {
              notifySuccess('Questions & Answers successfully extracted!');
            } else {
              notifyError('Failed to extract Questions & Answers');
            }
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      } catch (err) {
        notifyError(
          'Something went wrong while extracting Questions & Answers'
        );
      }
    } else {
      notifyError('Failed to initiate Questions & Answers extraction retry.');
    }
  };

  return (
    <div className={styles.container}>
      {showStepper && (
        <Stepper
          nextButtonText={'NEXT: ASSIGN →'}
          backButtonText={'← BACK: CREATE'}
          onNextClick={goToNextStep}
          activeTab={'confirm'}
          disableNextButton={!allQuestionsAreCompleted}
          onBackClick={goToBackStep}
        />
      )}

      <div className={styles.content}>
        <Typography variant="H-TEXT-2" color={colors.blue1}>
          Review & Confirm your Assignment
        </Typography>
        {data && data.length > 0 ? (
          <Typography
            variant="B-Text-1"
            color={colors.grey2}
            className={styles.subTitle}
          >
            Here's what we pulled from your exemplar. Click below to make any
            edits before assigning.
          </Typography>
        ) : isCheckingProgress ? (
          <div style={{ textAlign: 'center', paddingTop: '5%' }}>
            <CircularProgress size={40} />
          </div>
        ) : data &&
          data.length === 0 &&
          !isLoadingTranscriptionsAndSolutions ? (
          <TranscriptionsAndSolutionsErrorMessageBox
            showRetry={shouldRetry}
            onRetry={handleTranscriptionAndSolutionManualRetry}
          />
        ) : null}

        {currentPage && pages.length > 1 && (
          <FormControl variant="outlined" style={{ minWidth: 120 }}>
            <InputLabel id="page-select-label">Page</InputLabel>
            <Select
              labelId="page-select-label"
              id="page-select"
              value={currentPage?.id || ''}
              onChange={handlePageChange}
              label="Page"
            >
              {pages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <div className={styles.questionsAndAnswersContainer}>
          <div className={styles.questions}>
            <div className={styles.header}>
              <Typography variant="H-Text-2" color={colors.white}>
                Questions
              </Typography>
            </div>
            <div className={styles.questionsList}>
              {isNull(transcriptionsAndSolutionsData) ||
              isLoadingTranscriptionsAndSolutions ? (
                <div>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rect"
                      height={100}
                      animation="wave"
                      className={styles.skeleton}
                    />
                  ))}
                </div>
              ) : (
                transcriptionsAndSolutionsData.map(
                  ({ transcription, solution }, index) => (
                    <div
                      key={index}
                      className={cx(styles.question, {
                        [styles.active]: activeIndex === index
                      })}
                      onClick={() => setActiveIndex(index)}
                    >
                      <Typography variant="B-Text-1" color={colors.blue2}>
                        {index + 1}{' '}
                      </Typography>
                      {LATEX_EDITOR_ENABLED ? (
                        <>
                          <PreviewField initialValue={transcription} />
                        </>
                      ) : (
                        <>
                          <QuestionField
                            key={activeIndex}
                            initialValue={transcription}
                            readOnlyMode={true}
                            onChange={() => {}}
                          />
                        </>
                      )}

                      <div>
                        {includes(completedQuestions[currentPage.id], index) ? (
                          <CheckCircleIcon
                            className={cx(styles.icon, styles.completedIcon)}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            className={cx(styles.icon, styles.pendingIcon)}
                          />
                        )}
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
          <div className={styles.answers}>
            <div className={styles.header}>
              <Typography variant="H-Text-2" color={colors.white}>
                Answers
              </Typography>
            </div>
            <div className={styles.activeAnswer}>
              {activeIndex !== null && transcriptionsAndSolutionsData !== null && (
                <>
                  <div className={styles.completeButton}>
                    <Button
                      startIcon={<CheckCircleIcon />}
                      disabled={includes(
                        completedQuestions[currentPage.id],
                        activeIndex
                      )}
                      onClick={handleConfirmClick}
                    >
                      Confirm
                    </Button>
                  </div>
                  <div className={styles.questionField}>
                    {LATEX_EDITOR_ENABLED ? (
                      <>
                        <PreviewField
                          initialValue={
                            transcriptionsAndSolutionsData[activeIndex].solution
                          }
                        />
                      </>
                    ) : (
                      <>
                        <QuestionField
                          key={activeIndex}
                          initialValue={
                            transcriptionsAndSolutionsData[activeIndex].solution
                          }
                          readOnlyMode={true}
                          onChange={() => {}}
                        />
                      </>
                    )}
                  </div>
                  <div className={styles.actions}>
                    <IconButton
                      className={styles.deleteButton}
                      onClick={() => handleDelete({ index: activeIndex })}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      className={styles.deleteButton}
                      onClick={() => {
                        setEditOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <Button
          className={styles.addButton}
          startIcon={<AddIcon />}
          onClick={() => {
            setEditOpen(true);
            setAddNewTranscriptionAndSolution(true);
          }}
        >
          Add Question-Answer
        </Button>
        {CREATE_ASSIGNMENT_NEW_FLOW_ENABLED && (
          <Button
            className={styles.finishButton}
            disabled={!allQuestionsAreCompleted}
            onClick={onComplete}
          >
            Finish Reviewing
          </Button>
        )}
      </div>
      {editOpen && !addNewTranscriptionAndSolution && (
        <EditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          answer={transcriptionsAndSolutionsData[activeIndex]?.solution}
          question={transcriptionsAndSolutionsData[activeIndex]?.transcription}
          activeIndex={activeIndex}
          onConfirm={({ transcription, solution }) =>
            handleEditConfirm({ transcription, solution })
          }
        />
      )}
      {addNewTranscriptionAndSolution && editOpen && (
        <EditDialog
          open={editOpen}
          onClose={() => {
            closeEditDialog();
          }}
          setAddNewTranscriptionAndSolution={setAddNewTranscriptionAndSolution}
          answer={''}
          question={''}
          activeIndex={activeIndex}
          onConfirm={() => {}}
          inCreateMode={true}
          onCreate={({ transcription, solution }) =>
            handleTranscriptionAndSolutionCreate({
              transcription,
              solution
            })
          }
        />
      )}
    </div>
  );
};

export default Editor;
